import type { AddToCartSubmitData, OptionalPartSubmitData } from "~/features/addToCart/hooks/useAddToCartForm";
import {
  type AddToCartApiItem,
  type AddToCartItem,
  type AddToCartPartItem,
  type AddToCartResponseItem,
} from "~/features/cart/cart/api/AddToCart";
import { isKITProduct, type ProductData } from "~/domains/product/types/ProductData";

export type ExtendedAddToCartFormData = AddToCartSubmitData & { productCode: string; saleNetPrice?: number };

export type AddToCartResultFormItem = ExtendedAddToCartFormData | AddToCartItem | AddToCartApiItem;
export type AddToCartResultFormData = AddToCartResultFormItem[];

export interface AddToCartResultDecisionParams {
  formData: AddToCartResultFormData;
  productInfo?: ProductData;
  responseItems?: AddToCartResponseItem[];
  phaseOut2Enabled: boolean;
}

export interface MultipleEntriesRow {
  designation?: string;
  productCode: string;
  quantity: number;
  saleNetPrice?: number;
}

export function isExtendedSubmitData(item: unknown): item is ExtendedAddToCartFormData {
  return !!item && typeof item === "object" && "formValues" in item;
}

export function getQuantity(item: AddToCartResultFormItem): number {
  if (isExtendedSubmitData(item)) {
    return item.formValues.quantity;
  }

  return item.quantity;
}

export function shouldRenderMultipleEntriesResult({
  formData,
  productInfo,
  responseItems,
  phaseOut2Enabled,
}: AddToCartResultDecisionParams): boolean {
  if (formData.length > 1) {
    return true;
  }

  if (isKitRequest(formData, productInfo)) {
    return true;
  }

  return shouldUseResponseItemsForMultipleMode({ formData, productInfo, responseItems, phaseOut2Enabled });
}

export function getMultipleEntriesRows({
  formData,
  productInfo,
  responseItems,
  phaseOut2Enabled,
}: AddToCartResultDecisionParams): MultipleEntriesRow[] {
  if (formData.length === 0) {
    return [];
  }

  // Temporary - fix in 6.5
  // if (shouldUseResponseItemsForMultipleMode({ formData, productInfo, responseItems, phaseOut2Enabled })) {
  return (responseItems ?? []).map((item) => ({
    designation: item.designation,
    productCode: item.productCode,
    quantity: item.quantity,
  }));
  // }

  if (formData.length === 1) {
    return getRequestRows(formData[0]);
  }

  return formData.map((item) => getRequestRow(item));
}

function shouldUseResponseItemsForMultipleMode({
  formData,
  productInfo,
  responseItems,
  phaseOut2Enabled,
}: AddToCartResultDecisionParams): boolean {
  return (
    (phaseOut2Enabled &&
      formData.length === 1 &&
      !isKitRequest(formData, productInfo) &&
      (responseItems?.length ?? 0) > 1) ||
    ((responseItems?.length ?? 0) === 1 && responseItems![0].productCode !== formData[0].productCode)
  );
}

function isKitRequest(formData: AddToCartResultFormData, productInfo?: ProductData) {
  const firstItem = formData[0];

  if (!firstItem) {
    return false;
  }

  return isKITProduct(productInfo) || hasOptionalPartRows(firstItem);
}

function hasOptionalPartRows(item: AddToCartResultFormItem): boolean {
  if (isExtendedSubmitData(item)) {
    return Object.values(item.optionalParts).some((part) => part.formValues.selected);
  }

  return Array.isArray(item.optionalParts) && item.optionalParts.length > 0;
}

function getRequestRows(item: AddToCartResultFormItem): MultipleEntriesRow[] {
  return [getRequestRow(item), ...getOptionalPartRows(item)];
}

function getRequestRow(item: AddToCartResultFormItem): MultipleEntriesRow {
  return {
    productCode: item.productCode,
    quantity: getQuantity(item),
    saleNetPrice: "saleNetPrice" in item ? item.saleNetPrice : undefined,
  };
}

function getOptionalPartRows(item: AddToCartResultFormItem): MultipleEntriesRow[] {
  if (isExtendedSubmitData(item)) {
    return Object.values(item.optionalParts)
      .filter((part) => part.formValues.selected)
      .map((part) => ({
        productCode: part.formValues.productId,
        quantity: part.formValues.quantity,
      }));
  }

  return (item.optionalParts ?? []).map((part) => getOptionalPartRow(part));
}

function getOptionalPartRow(part: OptionalPartSubmitData | AddToCartPartItem): MultipleEntriesRow {
  if (isOptionalPartSubmitData(part)) {
    return {
      productCode: part.formValues.productId,
      quantity: part.formValues.quantity,
    };
  }

  return {
    productCode: part.productCode,
    quantity: part.quantity,
  };
}

function isOptionalPartSubmitData(part: OptionalPartSubmitData | AddToCartPartItem): part is OptionalPartSubmitData {
  return "formValues" in part;
}
